// src/shared_new/components/CostWaterfall.jsx
// Reusable Cost Waterfall Chart Component with Perl Theme

import React, { useMemo } from "react";
import { Paper, Box, Typography } from "@mui/material";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
} from "recharts";
import { theme } from "../../design/theme";

const { perlColors, perlTypography } = theme.custom;

// Chart colors
const chartColors = {
  blue50: "#0095FF",
  blue80: "#99D5FF",
};

/**
 * CostWaterfall - Waterfall chart showing cost build-up
 *
 * @param {Object} props
 * @param {Object} props.breakdown - Cost breakdown: { material, process, packaging, ohp, currency } (unused when rows provided)
 * @param {Array} props.rows - Optional array of cost rows: [{ key, label, value }]
 * @param {Object} props.spRow - Raw stored-proc row (optional, used to split packaging)
 * @param {string} props.title - Chart title (default: "Should-Cost Waterfall")
 * @param {Function} props.format - Custom value formatter
 * @param {Array} props.rows - Optional array of cost rows: [{ key, label, value }]
 * @param {Array} props.rows - Optional array of cost rows: [{ key, label, value }]
 */
export default function CostWaterfall({
  breakdown,
  rows = [],
  title = "Should-Cost Waterfall",
  format,
}) {
  const barColor = chartColors.blue80;
  const totalColor = chartColors.blue50;
  const BAR_RADIUS = [8, 8, 8, 8];
  const currency = breakdown?.currency || "USD/unit";

  // Use rows provided by the caller; no derivation/filters here
  const sourceRows = (rows || []).map((r, idx) => {
    const key = r.key || r.label || `row-${idx}`;
    return {
      key,
      label: r.label || r.name || key || `Row ${idx + 1}`,
      value: Number(r.value) || 0,
    };
  });

  const formatValue = (n) => {
    if (typeof format === "function") return format(n);
    const v = Number(n ?? 0);
    if (!Number.isFinite(v)) return "0.0000";
    return Math.abs(v) >= 1000 ? v.toFixed(2) : v.toFixed(4);
  };

  // // Derive split for packaging when available
  // const material = Number(breakdown?.material ?? 0);
  // const indirect = Number(spRow?.indirectmaterialcost ?? 0);
  // const consumablesSp = Number(spRow?.consumablescost ?? 0);
  // const packagingTotal = Number(breakdown?.packaging ?? 0);
  // const resolvedIndirect = indirect > 0 ? indirect : 0;
  // let resolvedConsumables = consumablesSp > 0 ? consumablesSp : packagingTotal;
  // if (resolvedConsumables <= 0 && packagingTotal > 0 && resolvedIndirect > 0) {
  //   resolvedConsumables = Math.max(0, packagingTotal - resolvedIndirect);
  // }
  // const processCombined = Number(breakdown?.process ?? 0) + resolvedIndirect;
  // const ohp = Number(breakdown?.ohp ?? 0);

  // const LABELS = {
  //   material: "Material",
  //   consumables: "Consumables",
  //   process: "Process",
  //   ohp: "OHP",
  //   total: "Total",
  // };

  // const ordered = ["material", "consumables", "process", "ohp"];

  // Build stacked data for the waterfall
  const data = useMemo(() => {
    // const values = {
    //   material,
    //   consumables: resolvedConsumables,
    //   process: processCombined,
    //   ohp,
    // };

    let running = 0;
    const rowsWithSteps = (sourceRows || []).map((row) => {
      const delta = Number(row.value) || 0;
      const step = {
        key: row.key,
        name: row.label || row.key,
        base: running,
        change: delta,
        totalChange: 0,
        displayCum: running + delta,
        total: false,
        barColor: barColor,
      };
      running += delta;
      return step;
    });

    const total = running;

    rowsWithSteps.push({
      key: "total",
      name: "Total",
      base: 0,
      change: 0,
      totalChange: total,
      displayCum: total,
      total: true,
      barColor: totalColor,
    });

    return rowsWithSteps;
  }, [sourceRows, barColor, totalColor]);

  const TooltipContent = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload || {};
    const isTotal = !!row.total;
    const barVal = isTotal ? row.totalChange : row.change;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.25,
          bgcolor: perlColors.surface,
          border: `1px solid ${perlColors.border}`,
          borderRadius: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            ...perlTypography.headline,
            color: perlColors.textPrimary,
            mb: 0.5,
          }}
        >
          {row.name}
        </Typography>
        <Typography variant="body2" sx={{ ...perlTypography.label, color: perlColors.textPrimary }}>
          Value: ${formatValue(barVal)} {currency}
        </Typography>
        <Typography
          variant="body2"
          sx={{ ...perlTypography.label, color: perlColors.textSecondary }}
        >
          {isTotal ? "Total" : "Cumulative"}: ${formatValue(row.displayCum)} {currency}
        </Typography>
      </Paper>
    );
  };

  const StepValueLabel = (props) => {
    const { x = 0, y = 0, width = 0, value, index } = props || {};
    const datum = data?.[index] || {};
    if (!Number.isFinite(value) || datum.total) return null;

    return (
      <text
        x={x + width / 2}
        y={y - 6}
        textAnchor="middle"
        style={{
          fontFamily: perlTypography.family,
          fontSize: 12,
          fontWeight: 400,
          fill: perlColors.textPrimary,
        }}
      >
        ${formatValue(datum.change)}
      </text>
    );
  };

  const TotalValueLabel = (props) => {
    const { x = 0, y = 0, width = 0, value, index } = props || {};
    const datum = data?.[index] || {};
    if (!datum.total || !Number.isFinite(value)) return null;

    return (
      <text
        x={x + width / 2}
        y={y - 6}
        textAnchor="middle"
        style={{
          fontFamily: perlTypography.family,
          fontSize: 12,
          fontWeight: 400,
          fill: perlColors.textPrimary,
        }}
      >
        ${formatValue(datum.totalChange)}
      </text>
    );
  };

  const wrapLabel = (label, maxChars = 12, maxLines = 2) => {
    const text = String(label ?? "");
    if (!text) return [""];
    const words = text.split(/\s+/);
    const lines = [];
    let current = "";

    words.forEach((word) => {
      const next = current ? `${current} ${word}` : word;
      if (next.length <= maxChars) {
        current = next;
        return;
      }
      if (current) lines.push(current);
      current = word;
    });

    if (current) lines.push(current);

    if (lines.length > maxLines) {
      const truncated = lines.slice(0, maxLines);
      const last = truncated[maxLines - 1];
      truncated[maxLines - 1] =
        last.length > maxChars - 1 ? `${last.slice(0, maxChars - 1)}…` : `${last}…`;
      return truncated;
    }

    return lines;
  };

  const XAxisTick = ({ x = 0, y = 0, payload }) => {
    const maxChars = data.length > 6 ? 8 : 12;
    const lines = wrapLabel(payload?.value, maxChars, 2);

    return (
      <text
        x={x}
        y={y + 10}
        textAnchor="middle"
        style={{
          fontFamily: perlTypography.family,
          fontSize: 12,
          fontWeight: 400,
          fill: perlColors.textSecondary,
        }}
      >
        {lines.map((line, idx) => (
          <tspan key={`${line}-${idx}`} x={x} dy={idx === 0 ? 0 : 13}>
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  return (
    <Paper
      data-report-waterfall-chart="true"
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${perlColors.border}`,
        bgcolor: perlColors.surface,
        height: "100%",
      }}
    >
      <Box sx={{ height: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 16, right: 16, bottom: 10, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={perlColors.gridLine || "#303436"} />
            <XAxis
              dataKey="name"
              tick={<XAxisTick />}
              stroke={perlColors.textSecondary}
              interval={0}
              minTickGap={0}
              height={52}
              tickMargin={8}
            />
            <YAxis
              domain={[0, (max) => max * 1.12]}
              tick={{
                fontFamily: perlTypography.family,
                fontSize: 14,
                fontWeight: 400,
                fill: perlColors.textSecondary,
              }}
              stroke={perlColors.textSecondary}
              tickFormatter={(v) => formatValue(v)}
            />
            <Tooltip content={<TooltipContent />} />

            {/* invisible base */}
            <Bar dataKey="base" stackId="a" fill="transparent" isAnimationActive={false} />

            {/* step bars */}
            <Bar
              dataKey="change"
              stackId="a"
              radius={BAR_RADIUS}
              shape={(props) => {
                const { x, y, width, height, index } = props;
                const datum = data[index];
                if (!datum || datum.total) return null;

                return (
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={datum.barColor}
                    rx={BAR_RADIUS[0]}
                    ry={BAR_RADIUS[0]}
                  />
                );
              }}
            >
              <LabelList content={<StepValueLabel />} />
            </Bar>

            {/* final total bar */}
            <Bar dataKey="totalChange" stackId="a" radius={BAR_RADIUS} fill={totalColor}>
              <LabelList content={<TotalValueLabel />} />
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
